import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import dropdownItemHOC from "./hoc/dropdown-item-hoc";
import dropdownButtonHOC from "./hoc/dropdown-button-hoc";
import CustomScrollbar from "../scrollbar/customScrollbar";

import defaultStyles from "./dropdown2.scss";

const trim = null;

function DropdownWrapper(props) {
  const wrapperRef = useRef(null);
  const {
    size,
    dropdownTitle,
    dropdownHeader,
    dropdownBody,
    dropdownFooter,
    scrolled,
    isOpen,
    wrapDropdownBody,
    wrapDropdownFooter,
    handleClickItem,
    handleClickButton,
    styles,
    className,
    arrow,
    openByDefault = false,
    fixedPosition = false,
  } = props;

  const [dropdownIsOpen, setDropdownIsOpen] = useState(openByDefault);
  const [fixedStyle, setFixedStyle] = useState({});

  useEffect(() => {
    if (isOpen) {
      setDropdownIsOpen(true);
    }
  }, [isOpen]);

  // Keep a ref to the latest toggle function so the stable HOC wrapper
  // always calls the current version without needing to be recreated.
  const toggleRef = useRef(null);
  const toggleDropdownVisibility = () => {
    if (fixedPosition && !dropdownIsOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const availableHeight = window.innerHeight - rect.bottom - 16;
      setFixedStyle({
        position: "fixed",
        top: rect.bottom + "px",
        left: rect.left + "px",
        width: "max-content",
        zIndex: 9999,
        "--max-dropdown-height": availableHeight + "px",
      });
    }
    setDropdownIsOpen(!dropdownIsOpen);
  };
  toggleRef.current = toggleDropdownVisibility;

  // Stable callback: identity never changes, so the HOC is only created once
  // per unique dropdownBody.component, preventing remounts on re-render.
  const stableToggle = useCallback(() => toggleRef.current(), []);

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setDropdownIsOpen(false);
    }
  };

  const DropdownBody = useMemo(
    () =>
      wrapDropdownBody
        ? dropdownItemHOC(dropdownBody.component, stableToggle)
        : dropdownBody?.component,
    [wrapDropdownBody, dropdownBody?.component, stableToggle]
  );
  const DropdownFooter = useMemo(
    () =>
      wrapDropdownFooter
        ? dropdownButtonHOC(dropdownFooter?.component, stableToggle)
        : dropdownFooter?.component,
    [wrapDropdownFooter, dropdownFooter?.component, stableToggle]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const arrowClassName = arrow
    ? `${defaultStyles.arrow} ${dropdownIsOpen ? defaultStyles.open : ""}`
    : null;

  let body = scrolled ? (
    <>
      <CustomScrollbar
        style={{
          height: props.height || 200,
        }}
        styles={styles}
      >
        <DropdownBody
          handleClickItem={handleClickItem}
          {...dropdownBody?.props}
        />
      </CustomScrollbar>
    </>
  ) : (
    <DropdownBody handleClickItem={handleClickItem} {...dropdownBody?.props} />
  );

  return (
    <>
      <div
        ref={wrapperRef}
        className={`${defaultStyles.dropdownWrapper} ${styles[size]} ${
          trim ? styles.trim : ""
        } ${styles[className]}`}
      >
        <div
          className={`${styles.dropdownTitle} ${defaultStyles.dropdownTitle} ${arrowClassName}`}
          onClick={toggleDropdownVisibility}
        >
          {dropdownTitle}
        </div>
        {dropdownIsOpen && (
          <>
            <div
              className={`${styles.dropdown} ${defaultStyles.dropdown} `}
              style={fixedPosition ? fixedStyle : undefined}
            >
              <div
                className={`${styles.dropdownHeader} ${defaultStyles.dropdownHeader}`}
              >
                {dropdownHeader}
              </div>
              <div
                className={`${defaultStyles.dropdownBody} ${styles.dropdownBody}`}
              >
                {dropdownBody?.component && body}
              </div>
              <div
                className={`${defaultStyles.dropdownFooter} ${styles.dropdownFooter}`}
              >
                {dropdownFooter?.component && (
                  <DropdownFooter
                    handleClickButton={handleClickButton}
                    {...dropdownFooter?.props}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default DropdownWrapper;
